// 🖊️ ORQUESTRAÇÃO DA ASSINATURA DIGITAL — B212 Fatia 4 (AI_126)
// Sistema: HEPATO TX RONDÔNIA
// ─────────────────────────────────────────────────────────────
// Liga as 3 fatias já prontas, do ponto de vista do navegador:
//   F2 (render)  → /api/gerar-pdf      : HTML do documento → PDF binário
//   F1 (broker)  → /api/assinar        : link OAuth do BIRD ID → status → assina o HASH
//   F3 (montagem)→ assinaturaCliente   : preparar → enviarHash → embutir (PAdES)
//
// O ModalAssinatura é quem dirige a máquina de estados (mostra o QR, espera a
// autorização no BIRD ID); este módulo só expõe os "verbos" de rede tipados.
// Privacidade (LGPD): só o HASH vai à BRy; o conteúdo do paciente nasce e fica
// na nossa infra (render em São Paulo + montagem em gru1).
//
// CFM 2454/2026 Art. 17: todas as chamadas via authFetch (ID Token + retry 401).
// ─────────────────────────────────────────────────────────────

import { postAutenticado } from "../../../lib/authFetch";
import {
  assinarPdfPorHash,
  DadosAssinaturaWidget,
  ResultadoAssinatura,
} from "./assinaturaCliente";

const SESSAO_EXPIRADA = "Sessão expirada — entre novamente para assinar.";

/** F2 — manda o HTML do documento e recebe o PDF binário (base64). */
export async function gerarPdfDoDocumento(html: string, tipo: string): Promise<string> {
  const r = await postAutenticado<{ pdfBase64: string; bytes: number }>(
    "/api/gerar-pdf",
    { html, tipo },
    "F4.gerarPdf"
  );
  if (!r) throw new Error(SESSAO_EXPIRADA);
  return r.pdfBase64;
}

/**
 * F1a — gera a URL OAuth do BIRD ID e a sessão PSC de MÚLTIPLAS assinaturas.
 * Devolve `lifetime` (segundos) e `numberOfDocuments` p/ o cofre reusar a sessão.
 */
export async function solicitarLinkBirdId(): Promise<{
  authUrl: string;
  sessao: string;
  lifetime: number;
  numberOfDocuments: number;
}> {
  const r = await postAutenticado<{
    authUrl: string;
    sessao: string;
    lifetime: number;
    numberOfDocuments: number;
  }>("/api/assinar", { acao: "link" }, "F4.link");
  if (!r) throw new Error(SESSAO_EXPIRADA);
  return r;
}

/** F1b — o médico já autorizou no app BIRD ID? (não gasta crédito) */
export async function consultarStatusAssinatura(sessao: string): Promise<boolean> {
  const r = await postAutenticado<{ signatureReady: boolean }>(
    "/api/assinar",
    { acao: "status", sessao },
    "F4.status"
  );
  if (!r) throw new Error(SESSAO_EXPIRADA);
  return r.signatureReady === true;
}

/**
 * F1c — troca o HASH pelo CMS da BRy (gasta 1 crédito; idempotente por requestId).
 * O `requestId` DEVE ser estável durante a tentativa (não regerar em retry).
 */
export async function assinarHashNoBroker(
  sessao: string,
  prescricaoId: string,
  requestId: string,
  hashB64: string
): Promise<string> {
  const r = await postAutenticado<{ cms: string; replay?: boolean }>(
    "/api/assinar",
    { acao: "assinar", prescricaoId, requestId, hashB64, sessao },
    "F4.assinar"
  );
  if (!r?.cms) throw new Error("A BRy não devolveu a assinatura (CMS vazio).");
  return r.cms;
}

/**
 * Assina o PDF ponta a ponta (dado que o médico JÁ autorizou no BIRD ID):
 * F3 (preparar) → broker (troca hash↔CMS) → F3 (embutir). Devolve o PDF assinado.
 */
export async function assinarDocumento(opts: {
  pdfBase64: string;
  sessao: string;
  prescricaoId: string;
  requestId: string;
  dados?: DadosAssinaturaWidget;
}): Promise<ResultadoAssinatura> {
  const { pdfBase64, sessao, prescricaoId, requestId, dados } = opts;
  return assinarPdfPorHash(
    pdfBase64,
    (hashB64) => assinarHashNoBroker(sessao, prescricaoId, requestId, hashB64),
    dados
  );
}

/** Baixa um PDF (base64) como arquivo no navegador. */
export function baixarPdfBase64(pdfBase64: string, nomeArquivo: string): void {
  const bytes = atob(pdfBase64);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
  const blob = new Blob([buffer], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo.endsWith(".pdf") ? nomeArquivo : `${nomeArquivo}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
